import React, { FunctionComponent, useEffect } from 'react';
import { Formik } from 'formik';
import { merge, isEmpty } from 'lodash';

import {
  ISignUpPhoneNumberEntryForm,
  SignUpPhoneNumberEntryFormDefinition,
} from '@jetslash/market-frontend-shared-core/src/forms/signUpPhoneNumberEntry';
import { ICurrentUser } from '@jetslash/market-frontend-shared-core/src/types/models/user';
import useInterval from '@jetslash/market-frontend-shared-core/src/utilities/useInterval';
import { IFormDefinition } from '@jetslash/market-frontend-shared-core/src/forms/formDefinitionInterface';
import { patchSignUpPhoneNumberEntry } from '@jetslash/market-frontend-shared-core/src/api/userApi';
import { ValidationErrorMessages } from '@jetslash/market-frontend-shared-core/src/utilities/form/types';
import { track } from '@/utility/eventTracking/eventTracker';
import { EVENT_SIGN_UP_STEP_VIEWED } from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/events';
import SignUpStepPropGroup from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/propertyGroupSerializers/SignUpStepPropGroup';
import { parseErrorMessagesFromResponse } from '@jetslash/market-frontend-shared-core/src/utilities/form/formErrorsFromErrorResponse';
import useSignUpWizard from '@/hooks/useSignUpWizard';
import Col from '../../layout/Col';
import Row from '../../layout/Row';
import Text from '../../typography/Text';
import CTAButton from '../../design/CTAButton';
import FormContainer from '../FormContainer';
import InputField from '../fields/InputField';
import FormErrorDisplay from '../FormErrorDisplay';
import Form from '../fields/Form';

interface IProps {
  signUpUser: ICurrentUser;
  loading: boolean;
  doNotPoll?: boolean;
  errors?: ValidationErrorMessages;
}

const SignUpPhoneNumberEntryFormContainer: FunctionComponent<IProps> = ({ loading, signUpUser, doNotPoll, errors }) => {
  const { signUpStepStartRequest, signUpStepSuccess, signUpStepFailure, updateSignUpUser } = useSignUpWizard();
  const formDefinition = new SignUpPhoneNumberEntryFormDefinition();

  useEffect(() => {
    track(EVENT_SIGN_UP_STEP_VIEWED, SignUpStepPropGroup.create(signUpUser, SignUpStepPropGroup.signUpSteps.SMS_ENTRY));
  }, []);

  const onSignUpPhoneNumberSubmit = async ({ unconfirmedPhone }: ISignUpPhoneNumberEntryForm): Promise<void> => {
    const signUpPhoneNumberData = { unconfirmedPhone };
    signUpStepStartRequest();

    try {
      await patchSignUpPhoneNumberEntry(signUpPhoneNumberData);
      signUpStepSuccess();
    } catch (err) {
      const formErrors = parseErrorMessagesFromResponse(err, formDefinition.HUMANIZATION_MAP);
      signUpStepFailure(formErrors);
    }
  };

  useEffect(() => {
    if (!doNotPoll) {
      updateSignUpUser();
    }
  }, []);

  useInterval(() => {
    if (!doNotPoll) {
      updateSignUpUser();
    }
  }, 5000);

  return (
    <div style={{ marginLeft: 5, marginRight: 5 }}>
      <Row>
        <Col span={24} textLeft style={{ marginLeft: 5, marginRight: 5, marginBottom: 10 }}>
          <Text>What&apos;s your mobile number? We&apos;ll send a text to verify your phone.</Text>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SignUpPhoneNumberEntryForm
            onSubmit={onSignUpPhoneNumberSubmit}
            signUpUser={signUpUser}
            loading={loading}
            validationErrors={errors}
          />
        </Col>
      </Row>
    </div>
  );
};

type SignUpPhoneNumberEntryFormProps = {
  onSubmit: (arg: ISignUpPhoneNumberEntryForm, actions: any) => void;
  loading: boolean;
  signUpUser: ICurrentUser;
  validationErrors?: ValidationErrorMessages;
};

class SignUpPhoneNumberEntryForm extends React.Component<SignUpPhoneNumberEntryFormProps> {
  // eslint-disable-next-line react/no-unused-class-component-methods
  textInput: { [key: string]: any } = {}; // keeps track of each ref on the form to enable moving to next field on enter

  formDefinition: IFormDefinition;

  constructor(props: SignUpPhoneNumberEntryFormProps) {
    super(props);
    this.formDefinition = new SignUpPhoneNumberEntryFormDefinition();
  }

  render() {
    const { onSubmit, loading, signUpUser, validationErrors } = this.props;

    return (
      <Formik
        validationSchema={this.formDefinition.validationSchema}
        initialValues={merge(this.formDefinition.initialValues(), {
          unconfirmedPhone: signUpUser.unconfirmedPhone || '',
        })}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, errors, values }) => (
          <FormContainer>
            <FormErrorDisplay errors={validationErrors} />
            <Form>
              <InputField name="unconfirmedPhone" loading={loading} type="phone" placeholder="Mobile phone" />
              <Row style={{ marginLeft: 5, marginRight: 5, marginTop: 10 }}>
                <Col span={24}>
                  <CTAButton
                    onClick={handleSubmit}
                    disabled={
                      !isEmpty(errors) || !values.unconfirmedPhone || !values.unconfirmedPhone.length || loading
                    }
                    text="Next"
                    icon="chevron-right"
                  />
                </Col>
              </Row>
            </Form>
          </FormContainer>
        )}
      </Formik>
    );
  }
}

export default SignUpPhoneNumberEntryFormContainer;
